import React, {useEffect, useState} from 'react';
import {useForm} from "react-hook-form";
import {Box, Button, CircularProgress, Grid, Typography, useToastProvider} from '@deere/fuel-react';
import {anyMatchInArray, generateTitle, getCurrentTotal, isNotEmptyArray} from '../shared/utils';
import {useAuthConfig} from "../shared/helper";
import {useHistory, useParams} from 'react-router-dom';
import {ANNUAL_RATING_STEPS} from "../shared/Constants";
import "../assets/ListItem.scss";
import {RatingProgramFunctionalArea} from './RatingProgramFunctionalArea';
import {RatingProgramCriteria} from "./RatingProgramCriteria";
import {RatingProgramReview} from "./RatingProgramReview";
import {Loading} from "../shared/Loading";
import {StepperComponent} from "../shared/StepperComponent";
import {ModalDialog} from "../shared/ModalDialog";
import {RatingProgramPublish} from "./RatingProgramPublish";

export const RatingProgramCreate = ({name}) => {
    let history = useHistory();
    let {editRatingProgramId} = useParams();
    let authAxios = useAuthConfig();
    const {enqueueToast} = useToastProvider();
    const {control, handleSubmit, getValues, reset, setValue} = useForm();
    const [isNext, setIsNext] = useState(false);
    const [stepId, setStepId] = useState();
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingNext, setIsSavingNext] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [nextButtonName, setNextButtonName] = useState("Save & Next");
    const [disable, setDisable] = useState(false);
    const [isFunctionalAreaError, setIsFunctionalAreaError] = useState(false);
    const [functionalAreaErrorDesc, setFunctionalAreaErrorDesc] = useState("");
    const [isSubFuncAreaError, setIsSubFuncAreaError] = useState(false);
    const [subFuncAreaErrorDesc, setSubFuncAreaErrorDesc] = useState("");

    const [ratingProgramId, setRatingProgramId] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [categoryId, setCategoryId] = useState();
    const [categoryName, setCategoryName] = useState();

    const initState = {
        id: "",
        status: "",
        functionalAreas: [],
        subFunctionalAreas: [],
        title: "",
        criteria: [],
        category: []
    }
    const [state, setState] = useState(initState);

    const getSteps = () => {
        return ANNUAL_RATING_STEPS;
    }

    const handleEditStep = (step, categoryId, categoryName) => {
        setActiveStep(step);
        setCategoryId(categoryId)
        setCategoryName(categoryName)
    };

    const steps = getSteps();

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return <RatingProgramFunctionalArea
                    control={control}
                    onSelectStep={handleStepId}
                    currentData={state}
                    setValue={setValue}
                    isFunctionalAreaError={isFunctionalAreaError}
                    functionalAreaErrorDesc={functionalAreaErrorDesc}
                    isSubFuncAreaError={isSubFuncAreaError}
                    subFuncAreaErrorDesc={subFuncAreaErrorDesc}
                />
            case 1:
                return <RatingProgramCriteria
                    control={control}
                    onSelectStep={handleStepId}
                    currentData={state}
                    onCriteriaUpdate={handleUpdateCriteria}
                    onSubCategoryUpdate={handleSubCategoryUpdate}
                    selectedEditCategoryId={categoryId}
                    selectedEditCategoryName={categoryName}
                />;
            case 2 :
                return <RatingProgramReview
                    currentData={state}
                    onEdit={handleEditStep}
                    onSelectStep={handleStepId}
                />
            case 3 :
                return <RatingProgramPublish
                    currentData={state}
                    onSelectStep={handleStepId}
                />

            default:
                return 'Unknown step';
        }
    }

    const handleStepId = (id) => {
        setStepId(id);
        if (id === 2) setNextButtonName("Publish")
        else if (id === 3) setNextButtonName("Published")
        else setNextButtonName("Save & Next")
    }

    const updateState = () => {
        if (isNext) {
            setIsSavingNext(false)
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } else {
            setIsSaving(false)
        }
    }

    const handleNext = () => {
        setIsNext(true)
    };

    const handleSelectedStep = (step) => {
        setActiveStep(step);
        if (step === 2) setNextButtonName("Publish")
        else if (step === 3) setNextButtonName("Published")
        else setNextButtonName("Save & Next")
    }

    const handleUpdateCriteria = (criteriaId, requiredFlag, weightValue, selectedFlag) => {
        const currentRow = state.criteria.filter(row => row.id === criteriaId)
        const updatedRow = currentRow.map(ele => {
            return {
                ...ele,
                isRequired: requiredFlag,
                weight: parseInt(weightValue),
                isSelected: selectedFlag
            }
        });
        const mergedArray = state.criteria.map(function (a) {
            return this[a.id] || a;
        }, updatedRow.reduce(function (r, a) {
            r[a.id] = a;
            return r;
        }, Object.create(null)));
        setValue(`${criteriaId}-crit-weight`, weightValue);
        setState({
            ...state,
            criteria: mergedArray
        })
    }

    const handleSubCategoryUpdate = (subCategoryId, weightValue) => {
        const updatedData = state.category.map(categoryEle => {
            const updatedSubCategories = categoryEle.subCategories.map(subCategoryEle => {
                if (subCategoryEle.id === subCategoryId) {
                    return {...subCategoryEle, weight: parseInt(weightValue)}
                } else
                    return subCategoryEle
            })
            return {...categoryEle, subCategories: updatedSubCategories}
        })
        setValue(`${subCategoryId}-sc-weight`, weightValue);
        setState({
            ...state,
            category: updatedData
        })
    }

    const onCancel = () => {
        history.push("/rating-programs");
    }

    const onDelete = async () => {
        await authAxios.delete(`/rating-program/${ratingProgramId}`)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Program has been deleted",
                    isCloseable: true
                });
                setTimeout(() => {
                    history.replace(`/rating-programs`);
                }, 2000);
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: error.response ? error.response.data[error.response.status] : "",
                    isPersistent: true
                });
            });
    }

    const validateFormData = () => {
        const functionalAreas = getValues("functionalAreas");
        const subFunctionalAreas = getValues("subFunctionalAreas");
        const subFunctionalIdsList = subFunctionalAreas.map(ele => ele.functionalAreaId)
        const functionalIdsList = functionalAreas.map(ele => ele.id)

        const isFunctionalAreasValid = anyMatchInArray(subFunctionalIdsList, functionalIdsList)
        const isSubFuncAreasValid = anyMatchInArray(functionalIdsList, subFunctionalIdsList)
        const isNotEmptySubFuncArea = isNotEmptyArray(subFunctionalAreas)
        const isNotEmptyFuncArea = isNotEmptyArray(functionalAreas)

        if (!isNotEmptyFuncArea || !isFunctionalAreasValid) {
            setIsFunctionalAreaError(true)
            setFunctionalAreaErrorDesc(!isFunctionalAreasValid ? "Choose all related functional areas" : "Please select a functional area")
        } else {
            setIsFunctionalAreaError(false)
            setFunctionalAreaErrorDesc("")
        }

        if (!isNotEmptySubFuncArea || !isSubFuncAreasValid) {
            setIsSubFuncAreaError(true)
            setSubFuncAreaErrorDesc(!isSubFuncAreasValid ? "Choose any one of related Sub functional areas" : "Please select a sub functional area")
        } else {
            setIsSubFuncAreaError(false)
            setSubFuncAreaErrorDesc("")
        }

        return isNotEmptyFuncArea && isNotEmptySubFuncArea && isFunctionalAreasValid && isSubFuncAreasValid;
    }

    const validateQuestionsData = (currentData) => {
        let isCategoryValid;
        let isSubCategoryValid;
        let isCriteriaValid;
        const updatedCategoryData = currentData.category.map(categoryEle => {
            const categoryTotal = categoryEle.subCategories.map(item => item.weight).reduce((partialSum, a) => partialSum + a, 0);
            const updatedSubCategories = categoryEle.subCategories.map(subCategoryEle => {
                const currentTotal = getCurrentTotal(currentData, subCategoryEle.id)
                if (currentTotal === subCategoryEle.weight) {
                    return {...subCategoryEle, isNotTotal: false}
                } else {
                    return {...subCategoryEle, isNotTotal: true}
                }
            })
            categoryEle = {...categoryEle, subCategories: updatedSubCategories}
            if (categoryTotal === 100) {
                return {...categoryEle, isNot100Error: false}
            } else {
                return {...categoryEle, isNot100Error: true}
            }
        })
        const updatedCriteriaData = currentData.criteria.map(criteriaEle => {
            if (criteriaEle.isSelected && criteriaEle.weight <= 0) {
                return {...criteriaEle, isNotValid: true}
            } else {
                return {...criteriaEle, isNotValid: false}
            }
        })
        isCriteriaValid = updatedCriteriaData.every(ele => ele.isNotValid === false)
        isCategoryValid = updatedCategoryData.every(ele => ele.isNot100Error === false)
        isSubCategoryValid = updatedCategoryData.map(item => item.subCategories).flat().every(ele => ele.isNotTotal === false)
        setState({
            ...state,
            category: updatedCategoryData,
            criteria: updatedCriteriaData
        })
        return isCategoryValid && isSubCategoryValid && isCriteriaValid
    }

    const ratingProgramPostData = (postData) => {
        setIsLoading(true);
        authAxios.post(`/rating-program`, postData)
            .then(res => {
                getRatingProgramData(res.data, true);
                enqueueToast({
                    variant: "success",
                    title: "Saved Successfully",
                    isCloseable: true
                });
                updateState()
                reset();
            }).catch((error) => {
            updateState()
            enqueueToast({
                variant: "error",
                title: "System Error",
                message: error.response ? error.response.data[error.response.status] : "",
                isPersistent: true
            });
            setActiveStep(postData.currentPage);
            setIsLoading(false);
        });
    }

    const ratingProgramUpdateData = (putData) => {
        authAxios.put(`/rating-program/${ratingProgramId}`, putData)
            .then(res => {
                updateState()
                enqueueToast({
                    variant: "success",
                    title: "Updated Successfully",
                    isCloseable: true
                });
            }).catch((error) => {
            enqueueToast({
                variant: "error",
                title: "System Error",
                message: error.response ? error.response.data[error.response.status] : "",
                isPersistent: true
            });
            setActiveStep(putData.currentPage);
        });
    }
    const processData = data => {
        setState({
            ...state,
            id: data.id,
            functionalAreas: data.functionalAreas,
            subFunctionalAreas: data.subFunctionalAreas,
            fiscalYear: data.fiscalYear,
            criteria: data.criteria,
            category: data.category,
            title: generateTitle(data.functionalAreas, data.subFunctionalAreas),
            status: data.publishStatus
        });
        setActiveStep(data.currentStep);
        setIsLoading(false);
    }

    const getRatingProgramData = async (id, isPost = false) => {
        await authAxios.get(`rating-program/${id}?isPost=${isPost}`)
            .then(res => {
                const data = res.data;
                setRatingProgramId(data.id);
                processData(data)
            }).catch((error) => {
                console.log(error);
            });
    }

    const onSubmit = async (data) => {
        if (stepId === 0) {
            if (!ratingProgramId) {
                const postData = {
                    fiscalYear: data.fiscalYear,
                    functionalAreas: data.functionalAreas.map(item => ({id: item.id, description: item.description})),
                    subFunctionalAreas: data.subFunctionalAreas,
                    currentStep: isNext ? stepId + 1 : stepId,
                    currentPage: stepId
                }
                const isValid = validateFormData();
                if (isValid) {
                    ratingProgramPostData(postData);
                }
            } else {
                const putData = {
                    ratingProgramId: ratingProgramId,
                    fiscalYear: data.fiscalYear,
                    functionalAreas: data.functionalAreas.map(item => ({id: item.id, description: item.description})),
                    subFunctionalAreas: data.subFunctionalAreas,
                    currentStep: isNext ? stepId + 1 : stepId,
                    currentPage: stepId
                }
                const isValid = validateFormData();
                if (isValid) {
                    ratingProgramUpdateData(putData);
                }
            }
        } else if (stepId === 1) {
            const putData = {
                ratingProgramId: state.id,
                fiscalYear: state.fiscalYear,
                functionalAreas: state.functionalAreas,
                subFunctionalAreas: state.subFunctionalAreas,
                criteria: state.criteria,
                category: state.category,
                currentStep: isNext ? stepId + 1 : stepId,
                currentPage: stepId
            }
            if (!isNext)
                ratingProgramUpdateData(putData);
            else {
                const isValid = validateQuestionsData(state);
                if (isValid) {
                    ratingProgramUpdateData(putData);
                }
            }
        } else if (stepId === 2) {
            const putData = {
                ratingProgramId: state.id,
                fiscalYear: state.fiscalYear,
                functionalAreas: state.functionalAreas,
                subFunctionalAreas: state.subFunctionalAreas,
                currentStep: stepId + 1,
                currentPage: stepId
            }
            ratingProgramUpdateData(putData);
        }
        setIsSavingNext(false)
        setIsSaving(false)
        setIsNext(false)
    }

    useEffect(() => {
        if (state.status === "Y") {
            setDisable(true);
        }
    }, [state]) // eslint-disable-next-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (ratingProgramId) {
            getRatingProgramData(ratingProgramId);
        }
        if (editRatingProgramId) {
            setIsLoading(true);
            getRatingProgramData(editRatingProgramId);
        }
        return () => {
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <form onSubmit={handleSubmit((data) => onSubmit(data))}>
            <Grid container alignItems="center">
                <Grid item xs={12} md={7} className="sticky-header" style={{height: 70}}>
                    <Typography variant="h1" component="h1">
                        {name}
                    </Typography>
                    <Typography variant="subtitle1">
                        {state.title ? `FY${state.fiscalYear}, ${state.title}` : ''}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={5} className="sticky-header" style={{height: 70}}>
                    <Box display="flex" justifyContent="flex-end">
                        {(editRatingProgramId) && (
                            <ModalDialog
                                titleText={"Delete Rating Program"}
                                content={<React.Fragment>Are you sure you want to delete this
                                    Rating Program?</React.Fragment>}
                                saveButtonText={"Delete"}
                                onSave={onDelete}
                                page={"Campaign"}
                            />
                        )}
                        {state.status === "Y" ? (
                            <Button variant="secondary" style={{marginRight: "1rem"}} size="large" onClick={() => {
                                onCancel()
                            }}>Back to Rating Programs</Button>
                        ) : (
                            <>
                                <Button variant="secondary" style={{marginRight: "1rem"}} size="large" onClick={() => {
                                    onCancel()
                                }}>Cancel</Button>
                                {(stepId !== 2) && (
                                    <Button variant="secondary" style={{marginRight: "1rem"}} size="large"
                                            type="submit" startIcon={isSaving ?
                                        <CircularProgress color="inherit" size="sm"/> : <></>}>Save</Button>
                                )}
                                <Button
                                    variant="primary"
                                    size="large"
                                    type="submit"
                                    startIcon={isSavingNext ? <CircularProgress color="inherit" size="sm"/> : <></>}
                                    onClick={() => {
                                        handleNext()
                                    }}>{nextButtonName}</Button>
                            </>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <StepperComponent
                        steps={steps}
                        disable={disable}
                        activeStep={activeStep}
                        onClick={handleSelectedStep}
                        disableStepNum={3}
                    />
                </Grid>
                {isLoading && (
                    <>
                        <Loading/>
                    </>
                )}
                {!isLoading && (
                    getStepContent(activeStep)
                )}
            </Grid>
        </form>
    );
}
